/**
 * DOM annotation elements shared by the non-iOS flight-map renderers (MapKit JS
 * on desktop web, Google Maps on Android). Both are replicas of the native
 * annotation views in FlightMapPlugin.swift so every platform draws the same
 * airport/plane markers.
 */

/**
 * DOM replica of the native `AirportAnnotationView` (FlightMapPlugin.swift):
 * a brand-purple dot inside a white ring, with the label in a tiny pill below.
 *
 * The root element is 0×0 with absolutely-positioned children so an anchor
 * placed at the coordinate lands exactly on the ring's center — the same
 * coordinate anchoring the native view achieves with `centerOffset`.
 */
export function airportAnnotationElement(label: string): HTMLElement {
  const root = document.createElement('div')
  root.style.cssText = 'position:relative;width:0;height:0;'

  const ring = document.createElement('div')
  ring.style.cssText =
    'position:absolute;left:-6px;top:-6px;width:12px;height:12px;' +
    'border-radius:9999px;background:#fff;' +
    'display:flex;align-items:center;justify-content:center;'
  const dot = document.createElement('div')
  dot.style.cssText =
    'width:8px;height:8px;border-radius:9999px;background:#801B69;'
  ring.appendChild(dot)
  root.appendChild(ring)

  if (label) {
    const pill = document.createElement('div')
    pill.textContent = label.toUpperCase()
    pill.style.cssText =
      'position:absolute;top:10px;left:0;transform:translateX(-50%);' +
      'padding:4px;background:#FAFAFA;border:1px solid #E5E5E5;' +
      'border-radius:4px;font-size:8px;line-height:10px;font-weight:600;' +
      'color:#0A0A0A;white-space:nowrap;text-align:center;'
    root.appendChild(pill)
  }

  return root
}

/**
 * DOM replica of the native `PlaneAnnotationView`: a white plane glyph rotated
 * to the route bearing, centered on its coordinate. The SVG points up (north),
 * so the rotation is the heading itself.
 */
export function planeAnnotationElement(headingDeg: number): HTMLElement {
  const root = document.createElement('div')
  root.style.cssText = 'position:relative;width:0;height:0;'

  const icon = document.createElement('div')
  icon.style.cssText =
    'position:absolute;left:-24px;top:-24px;width:48px;height:48px;' +
    `transform:rotate(${headingDeg}deg);` +
    'filter:drop-shadow(0 1px 2px rgba(0,0,0,0.6));'
  icon.innerHTML =
    '<svg width="48" height="48" viewBox="0 0 24 24" fill="#fff" xmlns="http://www.w3.org/2000/svg">' +
    '<path d="M21 16v-2l-8-5V3.5c0-.83-.67-1.5-1.5-1.5S10 2.67 10 3.5V9l-8 5v2l8-2.5V19l-2 1.5V22l3.5-1 3.5 1v-1.5L13 19v-5.5l8 2.5z"/>' +
    '</svg>'
  root.appendChild(icon)

  return root
}
